"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "WorkspaceTargetGraphBuilder", {
    enumerable: true,
    get: function() {
        return WorkspaceTargetGraphBuilder;
    }
});
const _createDependencyMap = require("workspace-tools/lib/graph/createDependencyMap.js");
const _targetId = require("./targetId.js");
const _expandDepSpecs = require("./expandDepSpecs.js");
const _path = /*#__PURE__*/ _interop_require_default(require("path"));
const _TargetGraphBuilder = require("./TargetGraphBuilder.js");
const _TargetFactory = require("./TargetFactory.js");
const _plimit = /*#__PURE__*/ _interop_require_default(require("p-limit"));
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
//eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports -- mergician is a dual-mode library with CJS and ESM export but a single .d.ts file. Without type="module" on this packge.json typescript gets confused. See: https://github.com/microsoft/TypeScript/issues/50466
const { mergician } = require("mergician");
const DEFAULT_STAGED_TARGET_THRESHOLD = 50;
class WorkspaceTargetGraphBuilder {
    /**
   * Generates new `Target`, indexed by the id based on a new target configuration.
   *
   * @param id
   * @param targetDefinition
   */ async addTargetConfig(id, config = {}, changedFiles) {
        // Generates a target definition from the target config
        if (id.startsWith("//") || id.startsWith("#")) {
            const targetConfig = this.determineFinalTargetConfig(id, config);
            const target = this.targetFactory.createGlobalTarget(id, targetConfig);
            this.graphBuilder.addTarget(target);
            this.hasRootTarget = true;
            this.processStagedConfig(target, config, changedFiles);
        } else if (id.includes("#")) {
            const { packageName, task } = (0, _targetId.getPackageAndTask)(id);
            const targetConfig = this.determineFinalTargetConfig(id, config);
            const target = this.targetFactory.createPackageTarget(packageName, task, targetConfig);
            this.graphBuilder.addTarget(target);
            this.processStagedConfig(target, config, changedFiles);
        } else {
            const packages = Object.keys(this.packageInfos);
            for (const packageName of packages){
                const task = id;
                const targetConfig = this.determineFinalTargetConfig((0, _targetId.getTargetId)(packageName, task), config);
                const target = this.targetFactory.createPackageTarget(packageName, task, targetConfig);
                this.graphBuilder.addTarget(target);
                this.processStagedConfig(target, config, changedFiles);
            }
        }
    }
    /**
   * Merges
   * @param id The Id of the target to merge
   * @param config The TargetConfig settings that will be merged if this target has already been seen before
   * @returns The merged TargetConfig object.
   */ determineFinalTargetConfig(targetId, config) {
        let finalConfig = config;
        if (this.enableTargetConfigMerging && this.targetConfigMap.has(targetId)) {
            const existingConfig = this.targetConfigMap.get(targetId);
            finalConfig = this.deepCloneTargetConfig(existingConfig, config);
        }
        this.targetConfigMap.set(targetId, finalConfig);
        return finalConfig;
    }
    /**
   * Side effects function on the passed in target
   * @param parentTarget
   * @param config
   */ async processStagedConfig(parentTarget, config, changedFiles) {
        if (typeof config.stagedTarget === "undefined") {
            return;
        }
        if (typeof changedFiles === "undefined" || changedFiles.length === 0 || changedFiles.length > (config.stagedTarget.threshold ?? DEFAULT_STAGED_TARGET_THRESHOLD)) {
            return;
        }
        this.hasStagedTarget = true;
        // First convert the parent to be a NO-OP, not cached, and should run always
        parentTarget.type = "noop";
        parentTarget.cache = false;
        parentTarget.shouldRun = false;
        // Create a staged target for the parent target
        const id = (0, _targetId.getStagedTargetId)(parentTarget.task);
        const stagedTarget = this.graphBuilder.targets.has(id) ? this.graphBuilder.targets.get(id) : this.targetFactory.createStagedTarget(parentTarget.task, config.stagedTarget, changedFiles);
        // Add the staged target to the graph
        this.graphBuilder.addTarget(stagedTarget);
        // Add all the parent target dependencies as the staged dependencies as unique set
        const depSet = new Set(stagedTarget.dependencies);
        for (const dep of parentTarget.dependencies){
            depSet.add(dep);
        }
        stagedTarget.dependencies = Array.from(depSet);
        // If parent target has dependents, we need to throw an error
        if (parentTarget.dependents.length > 0) {
            throw new Error(`Parent target ${parentTarget.id} cannot have dependents when it has a staged target while running with a --since flag`);
        }
    }
    shouldRun(config, target) {
        if (typeof config.shouldRun === "function") {
            return config.shouldRun(target);
        }
        return true;
    }
    /**
   * Builds a scoped target graph for given tasks and packages
   *
   * Steps:
   * 1. expands the dependency specs from the target definitions
   * 2. sub-graph filtered from the full dependency graph
   * 3. filtering all targets to just only the ones listed in the sub-graph
   * 4. returns the sub-graph
   *
   * @param tasks
   * @param scope
   * @param priorities the set of global priorities for the workspace.
   */ async build(tasks, scope, priorities) {
        // Expands the dependency specs from the target definitions
        const fullDependencies = (0, _expandDepSpecs.expandDepSpecs)(this.graphBuilder.targets, this.dependencyMap);
        for (const [from, to] of fullDependencies){
            this.graphBuilder.addDependency(from, to);
        }
        const subGraphEntries = [];
        for (const task of tasks){
            if (scope) {
                for (const packageName of scope){
                    subGraphEntries.push((0, _targetId.getTargetId)(packageName, task));
                }
            } else {
                for (const packageName of Object.keys(this.packageInfos)){
                    subGraphEntries.push((0, _targetId.getTargetId)(packageName, task));
                }
            }
            if (this.hasRootTarget) {
                const globalTargetId = (0, _targetId.getTargetId)(undefined, task);
                if (this.graphBuilder.targets.has(globalTargetId)) {
                    subGraphEntries.push(globalTargetId);
                }
            }
            if (this.hasStagedTarget) {
                const stagedTargetId = (0, _targetId.getStagedTargetId)(task);
                if (this.graphBuilder.targets.has(stagedTargetId)) {
                    subGraphEntries.push(stagedTargetId);
                }
            }
        }
        // Add all the global priorities for individual targets
        if (priorities) {
            for (const priorityConfig of priorities){
                // Right now we are only handling global priorities where the package name is set
                if (priorityConfig.package) {
                    const targetId = (0, _targetId.getTargetId)(priorityConfig.package, priorityConfig.task);
                    const target = this.graphBuilder.targets.get(targetId);
                    if (target) {
                        target.priority = target.priority ? Math.max(target.priority, priorityConfig.priority) : priorityConfig.priority;
                    }
                }
            }
        }
        const subGraph = this.graphBuilder.subgraph(subGraphEntries);
        const limit = (0, _plimit.default)(8);
        const setShouldRunPromises = [];
        for (const target of subGraph.targets.values()){
            const config = this.targetConfigMap.get(target.id);
            if (config) {
                setShouldRunPromises.push(limit(async ()=>{
                    target.shouldRun = await this.shouldRun(config, target);
                }));
            }
        }
        await Promise.all(setShouldRunPromises);
        return {
            targets: subGraph.targets
        };
    }
    /**
   * Initializes the builder with package infos
   * @param root the root directory of the workspace
   * @param packageInfos the package infos for the workspace
   */ constructor(root, packageInfos, enableTargetConfigMerging){
        _define_property(this, "packageInfos", void 0);
        _define_property(this, "enableTargetConfigMerging", void 0);
        /** A cache of the dependencyMap for packages, used inside dependency expansion */ _define_property(this, "dependencyMap", void 0);
        _define_property(this, "graphBuilder", void 0);
        _define_property(this, "targetFactory", void 0);
        _define_property(this, "hasRootTarget", void 0);
        _define_property(this, "hasStagedTarget", void 0);
        _define_property(this, "targetConfigMap", void 0);
        _define_property(this, "deepCloneTargetConfig", void 0);
        this.packageInfos = packageInfos;
        this.enableTargetConfigMerging = enableTargetConfigMerging;
        this.hasRootTarget = false;
        this.hasStagedTarget = false;
        this.targetConfigMap = new Map();
        this.deepCloneTargetConfig = mergician({
            appendArrays: true,
            onCircular: ()=>{
                throw new Error(`Circular object reference detected in TargetConfig`);
            }
        });
        this.dependencyMap = (0, _createDependencyMap.createDependencyMap)(packageInfos, {
            withDevDependencies: true,
            withPeerDependencies: false
        });
        this.graphBuilder = new _TargetGraphBuilder.TargetGraphBuilder();
        this.targetFactory = new _TargetFactory.TargetFactory({
            root,
            packageInfos,
            resolve (packageName) {
                try {
                    return _path.default.dirname(packageInfos[packageName].packageJsonPath);
                } catch (e) {
                    throw new Error(`Cannot open package.json file for ${packageName}`);
                }
            }
        });
    }
}
